import type {
  FinanceDataReliability,
  Task,
  TaskFinancialState,
  TaskFinancialStage,
} from "../models/production";
import type { HierarchySnapshot } from "../shared/rollupTypes";

export type FinanceVisualGrouping = "task" | "department" | "episode" | "asset";
export type FinanceVisualStage = "award" | "assigned" | "in_progress" | "completed" | "live";
export type FinanceVisualSort = "highest_bid" | "lowest_margin" | "highest_labour" | "alphabetical";
export type FinanceVisualScope = "all" | "shots" | "assets";

export interface FinanceVisualTaskEntry {
  task: Task;
  financialState?: TaskFinancialState;
}

export interface FinanceVisualBucket {
  id: string;
  label: string;
  fullLabel: string;
  bidValue: number;
  personCost: number;
  grossMargin: number;
  grossMarginPct?: number;
  displayGrossMarginPct?: number;
  confidenceScore?: number;
  reliability: FinanceDataReliability;
  lowConfidenceTaskCount: number;
  overScheduleTaskCount: number;
  taskCount: number;
  warningLabel?: string;
  missingAttributedCost: boolean;
}

export interface FinanceVisualDataset {
  grouping: FinanceVisualGrouping;
  stage: FinanceVisualStage;
  sort: FinanceVisualSort;
  scope: FinanceVisualScope;
  buckets: FinanceVisualBucket[];
  totalBidValue: number;
  totalPersonCost: number;
  totalGrossMargin: number;
  totalGrossMarginPct?: number;
  lowConfidenceBucketCount: number;
  overScheduleBucketCount: number;
  reliability: FinanceDataReliability;
  helperText: string;
}

interface TaskStagePoint {
  bidValue: number;
  personCost: number;
  grossMargin: number;
  confidenceScore?: number;
  reliability: FinanceDataReliability;
  lowConfidence: boolean;
  overSchedule: boolean;
}

const DEPARTMENT_BY_TASK_TYPE: Record<string, string> = {
  supervisor: "Production",
  review: "Production",
  concept: "Matte Painting",
  dmp: "Matte Painting",
  comp: "Comp",
  roto: "Vendor",
  prep: "Vendor",
  gfx: "Graphics",
  cameratr: "Matchmove",
  objecttr: "Matchmove",
  ranim: "Matchmove",
  layout: "Layout",
  anim: "Animation",
  light: "Lighting",
  fx: "FX",
  model: "Asset Build",
  rig: "Asset Build",
  retop: "Asset Build",
  cloth: "Asset Build",
  texture: "Asset Build",
  photoscan: "Asset Build",
  fur: "Groom",
  groom: "Groom",
  lookdev: "Lookdev",
};

function roundMoney(value: number) {
  return Math.round(value * 100) / 100;
}

function average(values: number[]) {
  if (!values.length) return undefined;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function toKey(value?: string | null) {
  return (value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function titleCase(value?: string | null) {
  return (value || "")
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function getStageSnapshot(state: TaskFinancialState | undefined, stage: TaskFinancialStage) {
  return state?.stageSnapshots?.find((snapshot) => snapshot.stage === stage);
}

function resolveTaskReliability(state?: TaskFinancialState): FinanceDataReliability {
  return state?.financeReliability || "low";
}

function summarizeReliability(scores: Array<FinanceDataReliability>) {
  if (!scores.length) return "low" as FinanceDataReliability;
  if (scores.every((value) => value === "high")) return "high" as FinanceDataReliability;
  if (scores.some((value) => value === "low")) return "low" as FinanceDataReliability;
  return "medium" as FinanceDataReliability;
}

function deriveCompletedPoint(state: TaskFinancialState | undefined): TaskStagePoint | null {
  if (!state) return null;
  const finalSnapshot = state.finalActualSnapshot || getStageSnapshot(state, "final_actual");
  const bidValue = state.taskBidValueInMoney;
  const personCost = finalSnapshot?.labourCost ?? state.actualLabourCost;
  const grossMargin = finalSnapshot?.grossProfit ?? state.actualGrossProfit;
  if (typeof bidValue !== "number") return null;

  const safePersonCost = typeof personCost === "number" ? personCost : 0;
  const safeGrossMargin = typeof grossMargin === "number" ? grossMargin : bidValue - safePersonCost;
  return {
    bidValue,
    personCost: safePersonCost,
    grossMargin: safeGrossMargin,
    confidenceScore: state.financeConfidenceScore,
    reliability: resolveTaskReliability(state),
    lowConfidence: resolveTaskReliability(state) === "low",
    overSchedule: state.financialStage === "in_progress_over_schedule",
  };
}

function deriveAwardPoint(state: TaskFinancialState | undefined): TaskStagePoint | null {
  if (!state) return null;
  const snapshot = getStageSnapshot(state, "initial_bid_forecast");
  const bidValue = state.taskBidValueInMoney;
  const personCost = snapshot?.labourCost ?? state.forecastLabourCost;
  const grossMargin = snapshot?.grossProfit ?? state.forecastGrossProfit;
  if (typeof bidValue !== "number") return null;

  const safePersonCost = typeof personCost === "number" ? personCost : 0;
  const safeGrossMargin = typeof grossMargin === "number" ? grossMargin : bidValue - safePersonCost;
  return {
    bidValue,
    personCost: safePersonCost,
    grossMargin: safeGrossMargin,
    confidenceScore: state.financeConfidenceScore,
    reliability: resolveTaskReliability(state),
    lowConfidence: resolveTaskReliability(state) === "low",
    overSchedule: false,
  };
}

function deriveAssignedPoint(state: TaskFinancialState | undefined): TaskStagePoint | null {
  if (!state) return null;
  const snapshot = getStageSnapshot(state, "assigned_artist_forecast") || getStageSnapshot(state, "initial_bid_forecast");
  const bidValue = state.taskBidValueInMoney;
  const personCost = snapshot?.labourCost ?? state.forecastLabourCost;
  const grossMargin = snapshot?.grossProfit ?? state.forecastGrossProfit;
  if (typeof bidValue !== "number") return null;

  const safePersonCost = typeof personCost === "number" ? personCost : 0;
  const safeGrossMargin = typeof grossMargin === "number" ? grossMargin : bidValue - safePersonCost;
  return {
    bidValue,
    personCost: safePersonCost,
    grossMargin: safeGrossMargin,
    confidenceScore: state.financeConfidenceScore,
    reliability: resolveTaskReliability(state),
    lowConfidence: resolveTaskReliability(state) === "low",
    overSchedule: false,
  };
}

function deriveInProgressPoint(state: TaskFinancialState | undefined): TaskStagePoint | null {
  if (!state) return null;
  const bidValue = state.taskBidValueInMoney;
  if (typeof bidValue !== "number") return null;

  const started =
    (state.actualLoggedMinutes || 0) > 0 ||
    state.financialStage === "in_progress_on_target" ||
    state.financialStage === "in_progress_over_schedule" ||
    state.financialStage === "final_actual";

  if (started) {
    const personCost =
      state.financialStage === "final_actual"
        ? state.actualLabourCost ?? state.finalActualSnapshot?.labourCost ?? state.forecastLabourCost
        : state.forecastLabourCost ?? state.actualLabourCost;
    const grossMargin =
      state.financialStage === "final_actual"
        ? state.actualGrossProfit ?? state.finalActualSnapshot?.grossProfit ?? state.forecastGrossProfit
        : state.forecastGrossProfit ?? state.actualGrossProfit;

    const safePersonCost = typeof personCost === "number" ? personCost : 0;
    const safeGrossMargin = typeof grossMargin === "number" ? grossMargin : bidValue - safePersonCost;
    return {
      bidValue,
      personCost: safePersonCost,
      grossMargin: safeGrossMargin,
      confidenceScore: state.financeConfidenceScore,
      reliability: resolveTaskReliability(state),
      lowConfidence: resolveTaskReliability(state) === "low",
      overSchedule: state.financialStage === "in_progress_over_schedule",
    };
  }

  return deriveAssignedPoint(state) || deriveAwardPoint(state);
}

// Stage-aware chart values deliberately fall back through the lifecycle so the full bid
// composition remains visible even when only part of the project has reached a later stage.
function deriveCompletedOrLivePoint(stage: FinanceVisualStage, state: TaskFinancialState | undefined): TaskStagePoint | null {
  if (!state) return null;
  const completedPoint = deriveCompletedPoint(state);
  if (stage === "completed" && completedPoint) return completedPoint;

  const bidValue = state.taskBidValueInMoney;
  if (typeof bidValue !== "number") return completedPoint;

  if (state.financialStage === "final_actual" && completedPoint) {
    return completedPoint;
  }

  const personCost = state.forecastLabourCost ?? state.actualLabourCost;
  const grossMargin = state.forecastGrossProfit ?? state.actualGrossProfit;
  const safePersonCost = typeof personCost === "number" ? personCost : 0;
  const safeGrossMargin = typeof grossMargin === "number" ? grossMargin : bidValue - safePersonCost;

  return {
    bidValue,
    personCost: safePersonCost,
    grossMargin: safeGrossMargin,
    confidenceScore: state.financeConfidenceScore,
    reliability: resolveTaskReliability(state),
    lowConfidence: resolveTaskReliability(state) === "low",
    overSchedule: state.financialStage === "in_progress_over_schedule",
  };
}

function deriveTaskStagePoint(stage: FinanceVisualStage, state?: TaskFinancialState): TaskStagePoint | null {
  switch (stage) {
    case "award":
      return deriveAwardPoint(state);
    case "assigned":
      return deriveAssignedPoint(state) || deriveAwardPoint(state);
    case "in_progress":
      return deriveInProgressPoint(state) || deriveAssignedPoint(state) || deriveAwardPoint(state);
    case "completed":
      return deriveCompletedOrLivePoint("completed", state) || deriveInProgressPoint(state) || deriveAssignedPoint(state) || deriveAwardPoint(state);
    case "live":
    default:
      return deriveCompletedOrLivePoint("live", state) || deriveInProgressPoint(state) || deriveAssignedPoint(state) || deriveAwardPoint(state);
  }
}

function resolveDepartmentLabel(task: Task) {
  const key = toKey(task.taskType || task.pipelineStep);
  return DEPARTMENT_BY_TASK_TYPE[key] || titleCase(task.pipelineStep || task.taskType) || "Unmapped";
}

function resolveEpisodeLabel(task: Task, snapshot: HierarchySnapshot) {
  if (!task.episodeId) return "No Episode";
  const episode = (snapshot.episodes || []).find((entry) => entry.id === task.episodeId);
  return episode?.code || episode?.title || `Episode ${task.episodeId}`;
}

function resolveShotLabel(task: Task, snapshot: HierarchySnapshot) {
  if (!task.shotId) return "Project Task";
  const shot = (snapshot.shots || []).find((entry) => entry.id === task.shotId);
  return shot?.shotCode || shot?.title || "Unnamed Shot";
}

function resolveAssetLabel(task: Task, snapshot: HierarchySnapshot) {
  if (!task.assetId) {
    return "Shot Work";
  }
  const asset = (snapshot.assets || []).find((entry) => entry.id === task.assetId);
  return asset?.assetCode || asset?.assetName || "Unnamed Asset";
}

function resolveGroup(task: Task, snapshot: HierarchySnapshot, grouping: FinanceVisualGrouping) {
  switch (grouping) {
    case "task":
      return {
        id: task.id,
        label:
          task.assetId
            ? `${resolveAssetLabel(task, snapshot)} • ${task.title || task.taskType || task.id}`
            : task.shotId
              ? `${resolveShotLabel(task, snapshot)} • ${task.title || task.taskType || task.id}`
              : task.title || task.taskType || task.id,
      };
    case "episode":
      return {
        id: task.episodeId || "no-episode",
        label: resolveEpisodeLabel(task, snapshot),
      };
    case "asset":
      return {
        id: task.assetId || "shot-work",
        label: resolveAssetLabel(task, snapshot),
      };
    case "department":
    default:
      return {
        id: toKey(resolveDepartmentLabel(task)) || "unmapped",
        label: resolveDepartmentLabel(task),
      };
  }
}

function getHelperText(stage: FinanceVisualStage, grouping: FinanceVisualGrouping, scope: FinanceVisualScope) {
  const groupingLabel =
    grouping === "task"
      ? "task"
      : grouping === "department"
        ? "department"
        : grouping === "episode"
          ? "episode"
          : "asset";
  const scopeLabel = scope === "all" ? "all project tasks" : scope === "shots" ? "shot tasks only" : "asset tasks only";
  switch (stage) {
    case "award":
      return `Award view uses the original bid baseline, with labour based on department-average assumptions, grouped by ${groupingLabel} across ${scopeLabel}.`;
    case "assigned":
      return `Assigned view swaps in assigned-artist rates where known, while unassigned work stays on baseline assumptions, grouped by ${groupingLabel} across ${scopeLabel}.`;
    case "in_progress":
      return `In Progress view uses logged effort where work has started and falls back through earlier stages where it has not, grouped by ${groupingLabel} across ${scopeLabel}.`;
    case "completed":
      return `Completed view uses final actual labour cost where available and the latest safe fallback elsewhere, grouped by ${groupingLabel} across ${scopeLabel}.`;
    case "live":
    default:
      return `Live view blends final actuals for completed work with current forecast values for everything still in motion, grouped by ${groupingLabel} across ${scopeLabel}.`;
  }
}

function taskMatchesScope(task: Task, scope: FinanceVisualScope) {
  if (scope === "shots") return Boolean(task.shotId);
  if (scope === "assets") return Boolean(task.assetId);
  return true;
}

export function buildProjectFinanceVisualDataset(args: {
  snapshot: HierarchySnapshot;
  taskEntries: FinanceVisualTaskEntry[];
  grouping: FinanceVisualGrouping;
  stage: FinanceVisualStage;
  sort: FinanceVisualSort;
  scope: FinanceVisualScope;
}): FinanceVisualDataset {
  const buckets = new Map<
    string,
    {
      id: string;
      label: string;
      bidValue: number;
      personCost: number;
      grossMargin: number;
      confidenceScores: number[];
      reliabilities: FinanceDataReliability[];
      lowConfidenceTaskCount: number;
      overScheduleTaskCount: number;
      taskCount: number;
    }
  >();

  for (const entry of args.taskEntries) {
    if (!taskMatchesScope(entry.task, args.scope)) continue;
    const point = deriveTaskStagePoint(args.stage, entry.financialState);
    if (!point || point.bidValue <= 0) continue;

    const group = resolveGroup(entry.task, args.snapshot, args.grouping);
    const bucket =
      buckets.get(group.id) ||
      {
        id: group.id,
        label: group.label,
        bidValue: 0,
        personCost: 0,
        grossMargin: 0,
        confidenceScores: [],
        reliabilities: [],
        lowConfidenceTaskCount: 0,
        overScheduleTaskCount: 0,
        taskCount: 0,
      };

    bucket.bidValue += point.bidValue;
    bucket.personCost += point.personCost;
    bucket.grossMargin += point.grossMargin;
    if (typeof point.confidenceScore === "number") bucket.confidenceScores.push(point.confidenceScore);
    bucket.reliabilities.push(point.reliability);
    bucket.lowConfidenceTaskCount += point.lowConfidence ? 1 : 0;
    bucket.overScheduleTaskCount += point.overSchedule ? 1 : 0;
    bucket.taskCount += 1;
    buckets.set(group.id, bucket);
  }

  const rows = Array.from(buckets.values()).map<FinanceVisualBucket>((bucket) => {
    const grossMarginPct = bucket.bidValue > 0 ? (bucket.grossMargin / bucket.bidValue) * 100 : undefined;
    const confidenceScore = average(bucket.confidenceScores);
    const reliability = summarizeReliability(bucket.reliabilities);
    const missingAttributedCost =
      args.stage !== "award" && bucket.personCost === 0 && bucket.lowConfidenceTaskCount > 0 && bucket.bidValue > 0;
    const warningLabel =
      missingAttributedCost
        ? "Cost not attributed yet"
        : bucket.overScheduleTaskCount > 0
        ? `${bucket.overScheduleTaskCount} over-schedule`
        : bucket.lowConfidenceTaskCount > 0
          ? `${bucket.lowConfidenceTaskCount} low confidence`
          : undefined;

    return {
      id: bucket.id,
      label: bucket.label.length > 16 ? `${bucket.label.slice(0, 15)}…` : bucket.label,
      fullLabel: bucket.label,
      bidValue: roundMoney(bucket.bidValue),
      personCost: roundMoney(bucket.personCost),
      grossMargin: roundMoney(bucket.grossMargin),
      grossMarginPct,
      displayGrossMarginPct: missingAttributedCost ? undefined : grossMarginPct,
      confidenceScore,
      reliability,
      lowConfidenceTaskCount: bucket.lowConfidenceTaskCount,
      overScheduleTaskCount: bucket.overScheduleTaskCount,
      taskCount: bucket.taskCount,
      warningLabel,
      missingAttributedCost,
    };
  });

  rows.sort((a, b) => {
    switch (args.sort) {
      case "alphabetical":
        return a.fullLabel.localeCompare(b.fullLabel, undefined, { numeric: true, sensitivity: "base" });
      case "lowest_margin":
        return (a.grossMarginPct ?? Number.POSITIVE_INFINITY) - (b.grossMarginPct ?? Number.POSITIVE_INFINITY);
      case "highest_labour":
        return b.personCost - a.personCost || a.fullLabel.localeCompare(b.fullLabel, undefined, { numeric: true, sensitivity: "base" });
      case "highest_bid":
      default:
        return b.bidValue - a.bidValue || a.fullLabel.localeCompare(b.fullLabel, undefined, { numeric: true, sensitivity: "base" });
    }
  });

  const totalBidValue = rows.reduce((sum, row) => sum + row.bidValue, 0);
  const totalPersonCost = rows.reduce((sum, row) => sum + row.personCost, 0);
  const totalGrossMargin = rows.reduce((sum, row) => sum + row.grossMargin, 0);
  const totalGrossMarginPct = totalBidValue > 0 ? (totalGrossMargin / totalBidValue) * 100 : undefined;
  const lowConfidenceBucketCount = rows.filter((row) => row.lowConfidenceTaskCount > 0).length;
  const overScheduleBucketCount = rows.filter((row) => row.overScheduleTaskCount > 0).length;
  const reliability = summarizeReliability(rows.map((row) => row.reliability));

  return {
    grouping: args.grouping,
    stage: args.stage,
    sort: args.sort,
    scope: args.scope,
    buckets: rows,
    totalBidValue: roundMoney(totalBidValue),
    totalPersonCost: roundMoney(totalPersonCost),
    totalGrossMargin: roundMoney(totalGrossMargin),
    totalGrossMarginPct,
    lowConfidenceBucketCount,
    overScheduleBucketCount,
    reliability,
    helperText: getHelperText(args.stage, args.grouping, args.scope),
  };
}

// TODO(finance-visuals): add sequence, shot, and task-status grouping modes once managers ask for deeper drill-down.
// TODO(finance-visuals): add forecast-vs-actual comparison overlays and historical trend lines over time.
// TODO(finance-visuals): support exportable finance visual reports once the reporting snapshot layer becomes more forensic.
